import { useMemo, useState } from "react"
import { Input, Table, Tag, Tooltip } from "antd"
import type { TableColumnsType } from "antd"
import { Link } from "react-router-dom"
import { DOMAIN_LABELS, DOMAIN_SLUGS, catalog, type MetricDefinition } from "@csbi/metric-catalog"
import { WIDGET_LABELS } from "@csbi/widgets"
import { PageHeader } from "../../shared/components/page-header.js"

const PRIORITY_LABELS: Record<MetricDefinition["priority"], string> = {
  must: "Must Have",
  nice: "Nice to Have",
  unspecified: "Unspecified",
}

const VALUE_TYPE_LABELS: Record<MetricDefinition["valueType"], string> = {
  count: "Count",
  duration_sec: "Duration",
  ratio: "Ratio",
  timestamp: "Timestamp",
  text: "Text",
}

export function CatalogPage() {
  const [keyword, setKeyword] = useState("")

  const rows = useMemo(() => {
    const q = keyword.trim().toLowerCase()
    if (!q) return catalog
    return catalog.filter((m) =>
      [m.id, m.name, m.description, m.logic, DOMAIN_LABELS[m.domain]].some((s) => s.toLowerCase().includes(q)),
    )
  }, [keyword])

  const columns: TableColumnsType<MetricDefinition> = [
    {
      title: "Metric",
      dataIndex: "name",
      key: "name",
      render: (name: string, m) => (
        <span style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{name}</span>
          <span style={{ fontSize: 11, color: "var(--csbi-ink-muted)" }}>{m.id}</span>
        </span>
      ),
    },
    {
      title: "Domain",
      dataIndex: "domain",
      key: "domain",
      width: 130,
      filters: [...new Set(catalog.map((m) => m.domain))].map((d) => ({ text: DOMAIN_LABELS[d], value: d })),
      onFilter: (value, m) => m.domain === value,
      render: (_: unknown, m) => (
        <Link to={`/domain/${DOMAIN_SLUGS[m.domain]}`}>{DOMAIN_LABELS[m.domain]}</Link>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 96,
      render: (p: MetricDefinition["priority"]) => PRIORITY_LABELS[p],
    },
    {
      title: "Unit",
      dataIndex: "valueType",
      key: "valueType",
      width: 90,
      render: (v: MetricDefinition["valueType"]) => VALUE_TYPE_LABELS[v],
    },
    {
      title: "Widget",
      dataIndex: "defaultWidget",
      key: "defaultWidget",
      width: 150,
      ellipsis: true,
      render: (w: MetricDefinition["defaultWidget"]) => WIDGET_LABELS[w],
    },
    {
      title: "Dimensions",
      dataIndex: "dims",
      key: "dims",
      width: 150,
      render: (dims: readonly string[]) => (dims.length ? dims.join(" / ") : "—"),
    },
    {
      title: "Flags",
      key: "flags",
      width: 108,
      render: (_: unknown, m) => (
        <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
          {m.approximate && (
            <Tooltip title={m.caveat ?? "This value is an approximation."}>
              <Tag color="warning" style={{ marginInlineEnd: 0 }}>
                approx.
              </Tag>
            </Tooltip>
          )}
          {m.sensitive && (
            <Tooltip title="Returns real identities of customer staff — an elevated role is required to query it">
              <Tag color="error" style={{ marginInlineEnd: 0 }}>
                sensitive
              </Tag>
            </Tooltip>
          )}
        </span>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Metric Dictionary"
        extra={
          <Input.Search
            allowClear
            placeholder="Search name, id or description"
            style={{ width: 280 }}
            onChange={(e) => setKeyword(e.target.value)}
          />
        }
      />
      <Table<MetricDefinition>
        size="small"
        rowKey="id"
        columns={columns}
        dataSource={[...rows]}
        pagination={{ pageSize: 30, showSizeChanger: false, showTotal: (n) => `${n} metrics` }}
        expandable={{
          expandedRowRender: (m) => (
            <div style={{ fontSize: 12, lineHeight: 1.7, color: "var(--csbi-ink-secondary)" }}>
              <div>{m.description}</div>
              <div style={{ marginTop: 4 }}>
                <strong style={{ color: "var(--csbi-ink-primary)" }}>Logic: </strong>
                {m.logic}
              </div>
              {m.caveat && <div style={{ marginTop: 4, color: "var(--csbi-status-warning-ink)" }}>Caveat: {m.caveat}</div>}
              <div style={{ marginTop: 4, color: "var(--csbi-ink-muted)" }}>
                srv module {m.srvModule} · rolls up by {m.rollup} · dictionary row {m.xlsxRow}
                {m.source ? ` · source: ${m.source}` : ""}
              </div>
            </div>
          ),
        }}
      />
    </>
  )
}
